import React, { useState, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Context as CartContext } from '../../../../context/cart';
import { Context as RestaurantContext } from '../../../../context/restaurant';
import { useMenuPositions } from '../../hooks/useMenuPositions';
import MenuBar from '../menuBar';
import { getSelectedCategory } from '../../services/categories';
import TopBar from '../../../../layouts/place/topBar';
import ProductModal from './productModal';
import { getTranslatedData } from '../../../../localization';
import BottomModal from '../../../../components/modal/bottomModal';
import ConfirmButton from '../../../../components/buttons/confirmButton';
import { calculateCartSum } from '../../../../services/cartService';
import { useCurrency } from '../../../../hooks/useCurrency';
import { useBackPress } from '../../../../hooks/useBackPress';
import { roundDecimal } from '../../../../utils/roundingHelpers';
import { useLocalStorage } from '../../../../hooks/useLocalStorage';
import Product from '../product/product';

const OrderMenu = ({ productCategories, onlyDisplay }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currencySymbol } = useCurrency();

  const { windowHeight, scrollPosition, categoriesY, categoriesRef, onImagePositionChange } =
    useMenuPositions(productCategories);

  const { state: cartState, addProductToCart } = useContext(CartContext);
  const { state: restaurantState } = useContext(RestaurantContext);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDialogOpen, setProductDialogOpen] = useState(false);

  const { handleBackOnModalOpen, handleBackOnModalClose } = useBackPress('/menu', () =>
    setProductDialogOpen(false),
  );

  const [ageConfirmed, setAgeConfirmed] = useLocalStorage(
    `age-confirmed-${restaurantState.restaurant.id}`,
    false,
  );

  const cartSum = useMemo(() => {
    const cartSumTemp = calculateCartSum({ cart: cartState.cart }) || 0;
    const withAppliedDiscount = cartSumTemp - (cartState.totalDiscounted || 0);

    return withAppliedDiscount < 0 ? 0 : roundDecimal(withAppliedDiscount);
  }, [cartState.cart, cartState.totalDiscounted]);

  const onManualSelect = (categoryId, scrollerHeight) => {
    const categoryPosition = categoriesY[categoryId || ''];

    // eslint-disable-next-line no-undef
    window.scrollTo({
      top: scrollerHeight
        ? categoryPosition - 110 - scrollerHeight + 54 - 10
        : categoryPosition - 110,
      behavior: 'smooth',
    });
  };

  const renderCategoryMenu = (category, ind) => {
    return (
      <div
        key={`${ind}`}
        ref={(el) => {
          if (!el) {
            return;
          }

          const oldPos = categoriesRef.current[category.id];
          categoriesRef.current[category.id] = { offsetTop: el ? el.offsetTop : 0 };

          // Position change on initial load
          if (oldPos && oldPos.offsetTop !== el.offsetTop && ind === productCategories.length - 1) {
            onImagePositionChange();
          }
        }}
      >
        <h3 className="font-primarySemiBold text-primary text-2xl mb-6">
          {category.id === '_other'
            ? t('products.categoryOther')
            : getTranslatedData(category.name)}
        </h3>
        {(category.products || []).map((product) => {
          let totalInCart = 0;
          if (cartState.cart) {
            cartState.cart.forEach((cartItem) => {
              if (cartItem.productId === product.id) {
                totalInCart += cartItem.quantity;
              }
            });
          }

          return (
            <Product
              key={product.id}
              product={product}
              onSelect={() => {
                setSelectedProduct(product);
                setProductDialogOpen(true);
                handleBackOnModalOpen();
              }}
              totalInCart={totalInCart}
            />
          );
        })}
      </div>
    );
  };

  const selected = getSelectedCategory({ windowHeight, scrollPosition, categoriesY });

  return (
    <>
      <div className="flex flex-col justify-between flex-1">
        <div>
          <div className="sticky top-0 bg-white z-10 -mx-5 px-5">
            <TopBar title={t('header.menu')} onBack={() => navigate('/')} />
            <MenuBar
              productCategories={productCategories || []}
              selected={selected}
              onSelect={onManualSelect}
            />
          </div>
          <div className="mt-6">
            {(productCategories || []).map((category, ind) => {
              return renderCategoryMenu(category, ind);
            })}
          </div>
        </div>
        {!onlyDisplay && (
          <ConfirmButton
            onClick={() => {
              navigate('/cart');
            }}
            customText={`${t('buttons.viewCart')} ${(cartSum || 0).toFixed(2)} ${currencySymbol}`}
            sticky
            disabled={cartSum <= 0}
          />
        )}
      </div>
      <BottomModal
        noHeader
        noPadding
        content={
          <ProductModal
            product={selectedProduct}
            onAdd={(data) => {
              addProductToCart(data, restaurantState.discountCartService);
              setProductDialogOpen(false);
              handleBackOnModalClose();
            }}
            refresher={productDialogOpen}
            onClose={() => {
              setProductDialogOpen(false);
              handleBackOnModalClose();
            }}
            discountMenuService={restaurantState.discountMenuService}
            onlyDisplay={onlyDisplay}
            needConfirmAge={!ageConfirmed}
            restrictionsAge={restaurantState.restaurant.restrictionsAge}
            onAgeConfirmed={() => setAgeConfirmed(true)}
          />
        }
        onClose={() => {
          setProductDialogOpen(false);
          handleBackOnModalClose();
        }}
        onOutClick={() => {
          setProductDialogOpen(false);
          handleBackOnModalClose();
        }}
        open={productDialogOpen}
      />
    </>
  );
};

export default OrderMenu;
